import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout'
import { qrCodeService, type ScanResult } from '@/services/qrCodeService'
import { scheduleService } from '@/services/scheduleService'

/**
 * AdminAttendanceScanPage
 * ------------------------
 * M4: admin scans peserta QR codes at an offline event. Uses html5-qrcode
 * dynamically so Node test environments can mock the scanner.
 */

const SCANNER_ID = 'ktp-qr-scanner'

export const AdminAttendanceScanPage: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>()

    // Fetch schedules for the dropdown.
    const { data: schedulesResp } = useQuery({
        queryKey: ['batch-schedules', batchId],
        queryFn: () => (batchId ? scheduleService.getSchedulesByBatch(batchId) : Promise.resolve({ success: true, data: [] } as any)),
        enabled: !!batchId,
    })
    const schedules = (schedulesResp?.data as any[]) ?? []

    const [scheduleId, setScheduleId] = useState<string>('')
    const [scanning, setScanning] = useState(false)
    const [lastResult, setLastResult] = useState<ScanResult | null>(null)
    const [lastError, setLastError] = useState<string | null>(null)
    const scannerRef = useRef<any>(null)

    // When user selects a schedule, start the scanner.
    useEffect(() => {
        if (!scheduleId || scanning) return
        let cancelled = false

        const start = async () => {
            try {
                const mod = await import('html5-qrcode')
                if (cancelled) return
                const Html5Qrcode = mod.Html5Qrcode
                const scanner = new Html5Qrcode(SCANNER_ID)
                scannerRef.current = scanner
                await scanner.start(
                    { facingMode: 'environment' },
                    { fps: 10, qrbox: 250 },
                    async (decodedText: string) => {
                        try {
                            const res = await qrCodeService.scan(decodedText, scheduleId)
                            if (res.success && res.data) {
                                setLastResult(res.data)
                                setLastError(null)
                            } else {
                                setLastError(res.error?.message || 'Scan gagal')
                            }
                        } catch (err) {
                            setLastError((err as Error).message || 'Scan gagal')
                        }
                    },
                    () => {
                        // onScanFailure - ignore transient frame errors.
                    },
                )
                setScanning(true)
            } catch (err) {
                setLastError((err as Error).message || 'Tidak dapat mengakses kamera')
            }
        }
        start()

        return () => {
            cancelled = true
            if (scannerRef.current) {
                try {
                    scannerRef.current.stop?.().catch(() => { })
                } catch {
                    // best-effort cleanup
                }
            }
        }
    }, [scheduleId]) // eslint-disable-line react-hooks/exhaustive-deps

    // Allow a "manual" submission for tests / fallback.
    const [manualToken, setManualToken] = useState('')
    const submitManual = async () => {
        if (!scheduleId || !manualToken) return
        try {
            const res = await qrCodeService.scan(manualToken, scheduleId)
            if (res.success && res.data) {
                setLastResult(res.data)
                setLastError(null)
            } else {
                setLastError(res.error?.message || 'Scan gagal')
            }
        } catch (err) {
            setLastError((err as Error).message || 'Scan gagal')
        }
    }

    return (
        <div data-testid="admin-attendance-scan-page">
            <PageHeader
                title="Scan Absensi"
                subtitle="Arahkan kamera ke QR code peserta untuk mencatat kehadiran"
            />

            <div className="max-w-xl space-y-4">
                <div>
                    <label htmlFor="schedule-select" className="block text-sm font-medium mb-1">
                        Sesi
                    </label>
                    <select
                        id="schedule-select"
                        value={scheduleId}
                        onChange={(e) => setScheduleId(e.target.value)}
                        data-testid="schedule-select"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700"
                    >
                        <option value="">Pilih sesi...</option>
                        {schedules.map((s: any) => (
                            <option key={s.id} value={s.id}>
                                #{s.sessionNumber ?? '?'} — {s.title}
                            </option>
                        ))}
                    </select>
                </div>

                <div
                    id={SCANNER_ID}
                    data-testid="scanner-container"
                    className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg bg-gray-200/80 dark:bg-black/20 flex items-center justify-center text-gray-500 dark:text-gray-400"
                >
                    {!scheduleId && 'Pilih sesi untuk mulai scan'}
                </div>

                {/* Manual fallback (useful for tests / when camera unavailable) */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label htmlFor="manual-token" className="block text-sm font-medium mb-1">
                        Input QR manual (fallback)
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="manual-token"
                            type="text"
                            value={manualToken}
                            onChange={(e) => setManualToken(e.target.value)}
                            data-testid="manual-token"
                            className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 text-sm"
                        />
                        <button
                            type="button"
                            onClick={submitManual}
                            disabled={!scheduleId || !manualToken}
                            data-testid="manual-submit"
                            className="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-50"
                        >
                            Kirim
                        </button>
                    </div>
                </div>

                {lastResult && (
                    <div
                        role="status"
                        data-testid="scan-success"
                        className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 p-4"
                    >
                        Hadir: peserta {lastResult.studentId.slice(0, 8)}… pada sesi {lastResult.sessionId.slice(0, 8)}…
                    </div>
                )}

                {lastError && (
                    <div
                        role="alert"
                        data-testid="scan-error"
                        className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-4"
                    >
                        {lastError}
                    </div>
                )}
            </div>
        </div>
    )
}

export default AdminAttendanceScanPage
